import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-background p-6">
                    <div className="max-w-md w-full text-center">
                        {/* Animated Error Icon */}
                        <div className="relative mx-auto mb-8 w-28 h-28">
                            <div className="absolute inset-0 bg-destructive/10 rounded-full animate-ping opacity-30" />
                            <div className="relative w-28 h-28 bg-gradient-to-br from-destructive/20 to-destructive/5 rounded-full flex items-center justify-center border border-destructive/20">
                                <AlertTriangle className="h-12 w-12 text-destructive" strokeWidth={1.5} />
                            </div>
                        </div>

                        <h1 className="text-3xl font-bold tracking-tight mb-3">
                            Something went wrong
                        </h1>
                        <p className="text-muted-foreground leading-relaxed mb-2">
                            An unexpected error occurred. Don't worry, your data is safe.
                        </p>

                        {/* Error Details (collapsible in production) */}
                        {this.state.error && (
                            <div className="mb-8 mt-4 p-4 bg-muted/50 rounded-2xl border border-border/50 text-left">
                                <p className="text-xs font-mono text-muted-foreground break-all leading-relaxed">
                                    {this.state.error.message}
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                            <Button
                                onClick={this.handleReload}
                                className="rounded-full h-12 px-8 gap-2 font-semibold shadow-lg shadow-primary/20"
                            >
                                <RefreshCw className="h-4 w-4" />
                                Try Again
                            </Button>
                            <Button
                                variant="outline"
                                onClick={this.handleGoHome}
                                className="rounded-full h-12 px-8 gap-2 font-semibold"
                            >
                                <Home className="h-4 w-4" />
                                Go Home
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
